import React, { useState, useEffect } from 'react';
import apiService from '../../services/apiService';

const DeceasedDetailView = ({ record, formatCurrency, onClose }) => {
  const [familyDetails, setFamilyDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDetails = async () => {
      try { const data = await apiService.getDeceasedDetails(record.id); setFamilyDetails(data); }
      catch (e) { console.error('Error fetching deceased details:', e); }
      finally { setLoading(false); }
    };
    fetchDetails();
  }, [record.id]);

  if (loading) {
    return (
      <div className="text-center py-10">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
        <p className="text-slate-500">Loading details...</p>
      </div>
    );
  }

  const relationLabel = (r) => r === 'S' ? 'Son' : r === 'D' ? 'Daughter' : r === 'W' ? 'Wife' : r === 'H' ? 'Husband' : r;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="text-center pb-5 border-b border-slate-200">
        <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-pink-600 rounded-full mx-auto mb-3 flex items-center justify-center">
          <span className="text-2xl">🕊️</span>
        </div>
        <h2 className="text-2xl font-bold text-slate-900 mb-1">{record.deceased_name}</h2>
        <p className="text-slate-500 text-sm">{new Date(record.date_of_death).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Type', value: record.was_primary_member ? 'Primary Member' : 'Family Member', color: 'slate' },
          { label: 'Age at Death', value: record.age_at_death ? `${Math.floor(record.age_at_death)} yrs` : '—', color: 'slate' },
          { label: 'Estimated Cost', value: formatCurrency(record.cost || 0), color: 'emerald' },
          { label: 'Member ID', value: (() => { const ref = familyDetails?.membership_ref || familyDetails?.transfer_info?.membership_ref || record.membership_ref; return ref ? `#${ref}` : '—'; })(), color: 'slate' },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-center">
            <p className="text-slate-500 text-xs mb-1">{label}</p>
            <p className={`font-bold text-sm ${color === 'emerald' ? 'text-emerald-600' : 'text-slate-800'}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Death Information */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-slate-800 mb-3">Death Information</h3>
        <div className="space-y-2.5">
          {[
            { label: 'Date of Death', value: new Date(record.date_of_death).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) },
            { label: 'Cause of Death', value: record.cause_of_death || 'Not specified' },
            { label: 'Burial Details', value: record.burial_cremation_details || 'Not specified' },
          ].map(({ label, value }) => (
            <div key={label}>
              <label className="text-slate-400 text-xs">{label}</label>
              <p className="text-slate-800 text-sm">{value}</p>
            </div>
          ))}
          {(record.reported_by_name || record.reported_by_relationship) && (
            <div className="pt-2 border-t border-slate-200">
              <label className="text-slate-400 text-xs">Reported By</label>
              <p className="text-slate-800 text-sm">{record.reported_by_name || 'N/A'}{record.reported_by_relationship ? ` (${record.reported_by_relationship.replace(/_/g, ' ')})` : ''}</p>
            </div>
          )}
        </div>
      </div>

      {/* Funeral Details */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-slate-800 mb-3">Funeral Details</h3>
        <div className="space-y-2.5">
          {[
            { label: 'Funeral Date', value: record.funeral_date ? new Date(record.funeral_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : 'Not specified' },
            { label: 'Payment Authorized By', value: record.payment_authorized_by || 'Not specified' },
            { label: 'Payment Release Date', value: record.payment_release_date ? new Date(record.payment_release_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : 'Not specified' },
          ].map(({ label, value }) => (
            <div key={label}>
              <label className="text-slate-400 text-xs">{label}</label>
              <p className="text-slate-800 text-sm">{value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Documentation Status */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-slate-800 mb-3">Documentation Status</h3>
        <div className="space-y-2">
          {[
            { label: 'Medical Certificate of Cause of Death', received: record.medical_certificate_received },
            { label: 'Death Registration Confirmation', received: record.death_registration_received },
            { label: 'Hospital/GP Confirmation', received: record.hospital_gp_confirmation_received },
          ].map(({ label, received }) => (
            <div key={label} className="flex items-center justify-between">
              <label className="text-slate-500 text-sm">{label}</label>
              <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${received ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {received ? '✓ Received' : '✗ Not Received'}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Family Context */}
      {familyDetails && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-slate-800 mb-3">Family Context</h3>
          <div className="space-y-3">
            <div className="bg-white border border-slate-200 rounded-lg p-3">
              <label className="text-slate-400 text-xs">Primary Member</label>
              <p className="text-slate-800 font-medium">{familyDetails.primary_member_name || record.parent_member_name || 'N/A'}</p>
              {(familyDetails.membership_ref || record.membership_ref) && (
                <p className="text-slate-400 text-xs mt-0.5">Ref: #{familyDetails.membership_ref || record.membership_ref}</p>
              )}
            </div>
            {familyDetails.living_family?.length > 0 && (
              <div>
                <label className="text-slate-400 text-xs">Living Family Members ({familyDetails.living_family.length})</label>
                <div className="mt-1.5 space-y-1.5">
                  {familyDetails.living_family.map((fam, idx) => (
                    <div key={idx} className="flex justify-between items-center bg-white border border-slate-200 rounded-lg p-2.5">
                      <span className="text-slate-800 text-sm">{fam.name}</span>
                      <span className="text-slate-400 text-xs">{relationLabel(fam.relation)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {familyDetails.other_deceased?.length > 0 && (
              <div>
                <label className="text-slate-400 text-xs">Other Deceased ({familyDetails.other_deceased.length})</label>
                <div className="mt-1.5 space-y-1.5">
                  {familyDetails.other_deceased.map((dec, idx) => (
                    <div key={idx} className="flex justify-between items-center bg-red-50 border border-red-100 rounded-lg p-2.5">
                      <span className="text-slate-800 text-sm">{dec.name}</span>
                      <span className="text-red-600 text-xs">{new Date(dec.date_of_death).getFullYear()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Financial Summary */}
      {familyDetails?.financial && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-emerald-800 mb-3">Financial Contribution Summary</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-emerald-600 text-xs">Lifetime Contributions</label>
              <p className="text-slate-900 font-bold text-lg">{formatCurrency(familyDetails.financial.lifetime_contributions || 0)}</p>
            </div>
            <div>
              <label className="text-emerald-600 text-xs">Years Active</label>
              <p className="text-slate-900 font-bold text-lg">{familyDetails.financial.years_active || 0}</p>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Info */}
      {familyDetails?.transfer_info && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-blue-800 mb-2">📋 Membership Transferred</h3>
          <div className="space-y-1">
            <p className="text-blue-700 text-sm">Transferred to: <strong>{familyDetails.transfer_info.new_primary_name}</strong></p>
            <p className="text-blue-600 text-xs">Membership ref #{familyDetails.transfer_info.membership_ref} continues under new primary member</p>
            <p className="text-slate-400 text-xs">Active since {new Date(familyDetails.transfer_info.join_date).toLocaleDateString('en-GB')}</p>
          </div>
        </div>
      )}

      <div className="flex justify-end pt-4 border-t border-slate-200">
        <button onClick={onClose} className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-xl transition-all">Close</button>
      </div>
    </div>
  );
};

export default DeceasedDetailView;
